import { useEffect, useMemo, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { ReviewList } from "@/profitability/review";
import { useFindings } from "@/profitability/review/query";
import type { RecordObject, RecordRef } from "./objects";
import type { ReviewScope } from "./objects-context";

/** Pages (of 25) read without asking: 500 findings, then "Show more". */
const AUTO_PAGES = 20;
/**
 * The most record ids sent in one request; a longer list is narrowed here
 * instead, so the request stays a sensible length.
 */
const ID_LIMIT = 100;

/**
 * What one run flagged in one dataset, as review cards rated and closed in
 * place: the findings of the flagged rows the block shows, after the pane's
 * filters. Every page is read, up to a cap, with a count of what is left.
 */
export const RunFindingsBlock = ({
  scope,
  objects,
  dataset,
  recordIds,
  filtered,
  noun,
  selected,
  onOpenRecord,
}: {
  scope: ReviewScope;
  objects: RecordObject[];
  /** The block's dataset: findings of other datasets are not its own. */
  dataset: string;
  /** The ids of the flagged rows the block shows, after its filters. */
  recordIds: string[];
  /** Whether the pane's filters hide some of the dataset's flagged rows. */
  filtered: boolean;
  /** What the rows are, plural and lower case: "jobs". */
  noun: string;
  selected: RecordRef | null;
  onOpenRecord: (ref: RecordRef) => void;
}) => {
  const [pageLimit, setPageLimit] = useState(AUTO_PAGES);
  const findings = useFindings(
    scope.businessId,
    scope.reportId,
    "all",
    {
      runId: scope.runId,
      dataset,
      recordIds:
        filtered && recordIds.length <= ID_LIMIT ? recordIds : undefined,
    },
    { keepPrevious: true },
  );
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = findings;
  const pages = findings.data?.pages ?? [];
  const more = hasNextPage && pages.length >= pageLimit;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && pages.length < pageLimit)
      void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, pageLimit, pages.length]);
  const visible = useMemo(() => new Set(recordIds), [recordIds]);
  // The filters decide which rows show; a finding of a hidden row is not
  // this block's, whether or not the ids went with the request.
  const items = pages
    .flatMap((page) => page.findings)
    .filter((finding) => !filtered || visible.has(finding.record_id));
  if (findings.isPending) return <Skeleton className="m-3 h-40 rounded-lg" />;
  return (
    <Stack gap={3} align="stretch">
      <ReviewList
        businessId={scope.businessId}
        findings={items}
        objects={objects}
        selected={selected}
        onOpenRecord={onOpenRecord}
      />
      {more && (
        <Stack direction="row" align="center" justify="center" gap={3}>
          <Text variant="caption" tone="muted" className="tabular-nums">
            Showing {items.length} of {recordIds.length} flagged {noun}
          </Text>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPageLimit(pageLimit + AUTO_PAGES)}
          >
            Show more
          </Button>
        </Stack>
      )}
      {!more && hasNextPage && (
        <Text variant="caption" tone="muted" role="status">
          Loading more findings…
        </Text>
      )}
    </Stack>
  );
};
